import GroupIcon from "@mui/icons-material/GroupOutlined";
import { Paper, Stack, Typography } from "@mui/material";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { commaSeparated } from "@entrilia/shared/utilities/stringHelper";
import type { ApprovalRule } from "../../../../api/types/workflowTypes";
import useClientContext from "../../../../context/useClientContext";
import ObjectFilterDescriptionText from "../../../object-filters/ObjectFilterDescriptionText";
import ApprovalConfigurationContext from "./ApprovalConfigurationContext";
import {
  getApprovalConfigurationMethodDescription,
  getApprovalRuleObjectFilter,
  getApprovalRuleSelectOptions,
} from "./approvalConfigurationHelper";

interface Props {
  approvalRule: ApprovalRule;
}

const ApprovalRuleCard = ({ approvalRule }: Props) => {
  const { dictionaries } = useClientContext();
  const { approvalConfiguration, lookupOptionsByObjectType } = useDefinedContext(ApprovalConfigurationContext);

  const groupNames = approvalConfiguration.groups
    .filter((g) => approvalRule.groupIds.includes(g.id))
    .map((g) => g.name)
    .sort();

  const ruleFilter = getApprovalRuleObjectFilter(approvalRule);

  const fieldSource =
    approvalConfiguration.approvalRuleFieldSources.find(
      (source) =>
        (source.relatedObjectType ?? source.scope) === (approvalRule.relatedObjectType ?? approvalRule.fieldScope) &&
        source.fields.some((field) => field.id === approvalRule.fieldId)
    ) ??
    approvalConfiguration.approvalRuleFieldSources.find((source) =>
      source.fields.some((field) => field.id === approvalRule.fieldId)
    );

  const field = fieldSource?.fields.find((f) => f.id === approvalRule.fieldId);
  const selectOptions = getApprovalRuleSelectOptions(
    field,
    lookupOptionsByObjectType[approvalRule.relatedObjectType ?? approvalConfiguration.objectType ?? ""],
    dictionaries
  );

  const fieldName = field
    ? fieldSource
      ? `${fieldSource.label}.${field.name}`
      : field.name
    : (approvalRule.fieldId ?? "");

  return (
    <Paper variant="outlined" sx={{ p: 1 }}>
      <Stack spacing={1.5}>
        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <GroupIcon color="secondary" />
            <Typography variant="subtitle2" noWrap>
              {commaSeparated(...groupNames)}
            </Typography>
          </Stack>
          <Typography variant="caption" color="textSecondary" sx={{ pl: 3.5 }}>
            {getApprovalConfigurationMethodDescription(approvalRule.method)}
          </Typography>
        </Stack>

        {ruleFilter && approvalRule.fieldId && (
          <ObjectFilterDescriptionText
            fieldName={fieldName}
            filter={ruleFilter}
            selectOptions={selectOptions}
            startLabel="Where"
          />
        )}
      </Stack>
    </Paper>
  );
};

export default ApprovalRuleCard;
